import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useWorkloadStore, type WorkloadIndicatorType, type WorkloadDisplayMode } from '../../stores/workloadStore';
import MiniTimelineDemo from './MiniTimelineDemo';
import '../../styles/workload-mode-selector.css';

// ─── Mode definitions ────────────────────────────────────────────────────────

interface ModeOption {
  value: WorkloadIndicatorType;
  label: string;
  description: string;
  bestFor: string;
  recommended?: boolean;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'remaining_estimate',
    label: 'Remaining Estimate',
    description: 'Shows estimated hours minus logged worklogs for today and future days. Past days show a dash. Item tooltip shows Original Estimate, Logged, and Remaining hours. Task bars display remaining hours instead of the original estimate.',
    bestFor: 'Tracking how much work is left on tasks',
    recommended: true,
  },
  {
    value: 'availability',
    label: 'Availability',
    description: 'Shows free hours remaining after subtracting remaining work from capacity. Uses actual worklogs to calculate real availability. Past days show a dash. Item tooltip shows Original Estimate, Logged, and Remaining hours.',
    bestFor: 'Managers checking who has real bandwidth',
  },
  {
    value: 'workload',
    label: 'Original Estimate',
    description: 'Shows raw scheduled hours from task estimates. Ignores worklogs entirely. Task bars display the original estimated hours as configured in the item.',
    bestFor: 'Initial planning before work starts',
  },
  {
    value: 'issues',
    label: 'Number of Items',
    description: 'Shows count of tasks per person per day. No hours involved.',
    bestFor: 'Seeing task distribution regardless of size',
  },
];

// ─── Props ───────────────────────────────────────────────────────────────────

interface WorkloadModeSelectorDialogProps {
  anchorRect: { top: number; left: number; width: number; height: number };
  onClose: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

const WorkloadModeSelectorDialog: React.FC<WorkloadModeSelectorDialogProps> = ({
  anchorRect,
  onClose,
}) => {
  const indicatorType = useWorkloadStore((s) => s.indicatorType);
  const displayMode = useWorkloadStore((s) => s.displayMode);
  const setIndicatorType = useWorkloadStore((s) => s.setIndicatorType);
  const setDisplayMode = useWorkloadStore((s) => s.setDisplayMode);

  const [expandedMode, setExpandedMode] = useState<WorkloadIndicatorType | null>(null);
  const [closing, setClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Position the dialog below the trigger
  const dialogStyle: React.CSSProperties = {
    top: anchorRect.top + anchorRect.height + 8,
    right: Math.max(16, window.innerWidth - anchorRect.left - anchorRect.width),
  };

  // Close with animation
  const animatedClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, 130);
  }, [closing, onClose]);

  // Select a mode
  const handleSelect = useCallback((mode: WorkloadIndicatorType) => {
    setIndicatorType(mode);
    // Auto-close after short delay
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(animatedClose, 400);
  }, [setIndicatorType, animatedClose]);

  // Toggle "How it works"
  const toggleExpand = useCallback((e: React.MouseEvent, mode: WorkloadIndicatorType) => {
    e.stopPropagation();
    setExpandedMode((prev) => (prev === mode ? null : mode));
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') animatedClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [animatedClose]);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const showPills = indicatorType !== 'issues';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`rp-mode-selector-backdrop${closing ? ' rp-mode-selector-backdrop--closing' : ''}`}
        onClick={animatedClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className={`rp-mode-selector${closing ? ' rp-mode-selector--closing' : ''}`}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Choose Workload Mode"
      >
        {/* Header */}
        <div className="rp-mode-selector__header">
          <span className="rp-mode-selector__title">Choose Workload Mode</span>

          {showPills && (
            <div className="rp-mode-selector__pills">
              <button
                className={`rp-mode-selector__pill${displayMode === 'hours' ? ' rp-mode-selector__pill--active' : ''}`}
                onClick={() => setDisplayMode('hours')}
              >
                h
              </button>
              <button
                className={`rp-mode-selector__pill${displayMode === 'percent' ? ' rp-mode-selector__pill--active' : ''}`}
                onClick={() => setDisplayMode('percent')}
              >
                %
              </button>
            </div>
          )}

          <button className="rp-mode-selector__close" onClick={animatedClose} aria-label="Close">
            &#x2715;
          </button>
        </div>

        {/* Card list */}
        <div className="rp-mode-selector__body" role="radiogroup" aria-label="Workload indicator modes">
          {MODE_OPTIONS.map((option) => {
            const isSelected = indicatorType === option.value;
            const isExpanded = expandedMode === option.value;

            return (
              <div
                key={option.value}
                className={`rp-mode-card${isSelected ? ' rp-mode-card--selected' : ''}`}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => handleSelect(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(option.value);
                  }
                }}
              >
                {/* Radio */}
                <div className="rp-mode-card__radio">
                  <div className="rp-mode-card__radio-dot" />
                </div>

                {/* Content */}
                <div className="rp-mode-card__content">
                  <div className="rp-mode-card__name-row">
                    <span className="rp-mode-card__name">{option.label}</span>
                    {option.recommended && (
                      <span className="rp-mode-card__badge">Recommended</span>
                    )}
                  </div>

                  <div className="rp-mode-card__desc">{option.description}</div>
                  <div className="rp-mode-card__best-for">Best for: {option.bestFor}</div>

                  <button
                    className="rp-mode-card__toggle"
                    onClick={(e) => toggleExpand(e, option.value)}
                    aria-expanded={isExpanded}
                  >
                    How it works
                    <span className={`rp-mode-card__toggle-chevron${isExpanded ? ' rp-mode-card__toggle-chevron--open' : ''}`}>
                      &#x25BC;
                    </span>
                  </button>

                  <div className={`rp-mode-card__demo-wrapper${isExpanded ? ' rp-mode-card__demo-wrapper--open' : ''}`}>
                    {isExpanded && <MiniTimelineDemo mode={option.value} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default WorkloadModeSelectorDialog;
